import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, FileText, ChevronRight, Newspaper, Eye, BookOpen } from 'lucide-react';
import { editionService } from '../services/api';
import BotonDescargarPdf from '../components/BotonDescargarPdf';
import styles from './EditionsPage.module.css';

export default function EditionsPage() {
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('all');

  useEffect(() => {
    loadEditions();
  }, []);

  const loadEditions = async () => {
    try {
      setLoading(true);
      const data = await editionService.getAll();
      setEditions(data.filter(e => e.status === 'published'));
    } catch (error) {
      console.error('Error loading editions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatShortDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Obtener años disponibles
  const years = [...new Set(editions.map(e => 
    new Date(e.publishedAt).getFullYear()
  ))].sort((a, b) => b - a);

  // Filtrar por año
  const filteredEditions = selectedYear === 'all'
    ? editions
    : editions.filter(e => 
        new Date(e.publishedAt).getFullYear() === parseInt(selectedYear)
      );

  // Agrupar por mes
  const groupedByMonth = filteredEditions.reduce((acc, edition) => {
    const date = new Date(edition.publishedAt);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthName = date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    
    if (!acc[key]) {
      acc[key] = { name: monthName, editions: [] };
    }
    acc[key].editions.push(edition);
    return acc;
  }, {});

  // Última edición destacada
  const latestEdition = editions[0];

  return (
    <main className={styles.editionsPage}>
      {/* Header */}
      <header className={styles.header}>
        <div className="container">
          <div className={styles.headerIcon}>
            <Newspaper size={32} />
          </div>
          <h1 className={styles.title}>Ediciones Impresas</h1>
          <p className={styles.subtitle}>
            Accede a todas las ediciones de nuestro periódico en formato digital
          </p>
        </div>
      </header>

      {/* Edición destacada */}
      {latestEdition && (
        <section className={styles.featured}>
          <div className="container">
            <div className={styles.featuredCard}>
              <div className={styles.featuredCover}>
                {latestEdition.coverImage ? (
                  <img src={latestEdition.coverImage} alt={`Edición ${latestEdition.number}`} />
                ) : (
                  <div className={styles.coverPlaceholder}>
                    <Newspaper size={48} />
                    <span>Edición N° {latestEdition.number}</span>
                  </div>
                )}
              </div>
              <div className={styles.featuredContent}>
                <span className={styles.featuredLabel}>Última edición</span>
                <h2 className={styles.featuredTitle}>
                  Edición N° {latestEdition.number}
                </h2>
                <p className={styles.featuredDate}>
                  <Calendar size={16} />
                  {formatDate(latestEdition.publishedAt)}
                </p>
                <p className={styles.featuredDesc}>
                  {latestEdition.articles?.length || 0} artículos • {latestEdition.pages?.length || 0} páginas
                </p>
                <div className={styles.featuredActions}>
                  <Link 
                    to={`/ediciones/${latestEdition.number}`}
                    className="btn btn-primary"
                  >
                    <Eye size={18} />
                    Ver edición
                  </Link>
                  <Link
                    to={`/edicion/${latestEdition.id}/revista`}
                    className="btn btn-outline"
                    title="Abrir como revista digital"
                  >
                    <BookOpen size={18} />
                    Modo revista
                  </Link>
                  <BotonDescargarPdf edition={latestEdition} variant="outline" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Filtros */}
      <div className={styles.filters}>
        <div className="container">
          <div className={styles.filtersInner}>
            <h2 className={styles.filtersTitle}>Archivo de ediciones</h2>
            <div className={styles.yearFilter}>
              <label>Año:</label>
              <select 
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="all">Todos</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de ediciones */}
      <section className={styles.editionsList}>
        <div className="container">
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.loadingSpinner}></div>
              <p>Cargando ediciones...</p>
            </div>
          ) : Object.keys(groupedByMonth).length === 0 ? (
            <div className={styles.empty}>
              <p>No hay ediciones disponibles</p>
            </div>
          ) : (
            Object.entries(groupedByMonth)
              .sort(([a], [b]) => b.localeCompare(a))
              .map(([key, { name, editions: monthEditions }]) => (
                <div key={key} className={styles.monthGroup}>
                  <h3 className={styles.monthTitle}>{name}</h3>
                  <div className={styles.editionsGrid}>
                    {monthEditions.map(edition => (
                      <Link
                        key={edition.id}
                        to={`/ediciones/${edition.number}`}
                        className={styles.editionCard}
                      >
                        <div className={styles.editionCover}>
                          {edition.coverImage ? (
                            <img src={edition.coverImage} alt={`Edición ${edition.number}`} />
                          ) : (
                            <div className={styles.coverPlaceholder}>
                              <Newspaper size={32} />
                            </div>
                          )}
                        </div>
                        <div className={styles.editionInfo}>
                          <span className={styles.editionNumber}>
                            N° {edition.number}
                          </span>
                          <span className={styles.editionDate}>
                            {formatShortDate(edition.publishedAt)}
                          </span>
                          <span className={styles.editionMeta}>
                            <FileText size={12} />
                            {edition.pages?.length || 0} págs.
                          </span>
                        </div>
                        <div className={styles.editionHover}>
                          <span>Ver edición</span>
                          <ChevronRight size={16} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))
          )}
        </div>
      </section>

      {/* CTA Suscripción */}
      <section className={styles.ctaSection}>
        <div className="container">
          <div className={styles.ctaCard}>
            <div className={styles.ctaContent}>
              <h2>Recibe el periódico en tu correo</h2>
              <p>Suscríbete y recibe cada edición directamente en tu bandeja de entrada</p>
            </div>
            <form className={styles.ctaForm}>
              <input type="email" placeholder="Tu correo electrónico" />
              <button type="submit" className="btn btn-primary">
                Suscribirse
              </button>
            </form>
          </div>
        </div>
      </section>
      {/* Keyframes para el spinner del botón descargar */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );
}
